import { ListNsnCandidatesResponse } from '@/api/generatedApi';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { useSearchFlisRecords } from '@/hooks/api/flis-records/useSearchFlisRecords';
import { usePartFinderReportSuggestions } from '@/hooks/api/part-finder-reports/usePartFinderReportSuggestions';
import { useTenants } from '@/services/tenants/useTenants';
import { ApiErrorDisplay, getApiError } from '@/types/apiError';
import { AddAPhotoOutlined, PlaylistAddOutlined, Search } from '@mui/icons-material';
import { Autocomplete, Box, Button, Chip, CircularProgress, Divider, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FindFromImages } from './image-search/FindFromImages';
import { NewBatchModal } from './NewBatchModal';
import { NsnCandidatePicker, NsnPickerChoice } from './NsnCandidatePicker';
import { PartFinderPageObjectModel } from './partFinderPageObjectModel';
import { mergeSuggestions, PartNumberSuggestion, suggestionKey, suggestionLabel } from './partNumberSuggestions';
import { usePartFinderSearch } from './usePartFinderSearch';

const SuggestionOption = styled('li')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
});

const SuggestionText = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  minWidth: 0,
  flex: 1,
});

const SuggestionItemName = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const FormCard = styled('form')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  maxWidth: '680px',
}));

const SearchRow = styled(Box)({
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-start',
});

const FindPartButton = styled(Button)({
  height: '56px',
  flexShrink: 0,
  borderRadius: '2px',
  paddingLeft: '24px',
  paddingRight: '24px',
  whiteSpace: 'nowrap',
});

const FormFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  paddingTop: '4px',
  color: theme.palette.text.secondary,
}));

const FooterActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
});

const FindMultipleLink = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.875rem',
  color: theme.palette.primary.main,
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
}));

export const PartFinderForm = () => {
  const [partNumber, setPartNumber] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<ApiErrorDisplay>();
  const [findMultipleOpen, setFindMultipleOpen] = useState(false);
  const partImageInputReference = useRef<HTMLInputElement>(null);
  const [nsnListing, setNsnListing] = useState<{ partNumber: string; listing: ListNsnCandidatesResponse }>();
  const lookupToken = useRef(0);
  const { runSearch, runSearchForNsns, searchOrListNsnCandidates, isPending } = usePartFinderSearch();
  const { suggestions, isFetching: isLoadingReports } = usePartFinderReportSuggestions(partNumber);
  const { flisRecords, isFetching: isLoadingFlis } = useSearchFlisRecords(partNumber);
  const { getPath } = useTenants();
  const navigate = useNavigate();

  const options = useMemo(() => mergeSuggestions(suggestions, flisRecords), [suggestions, flisRecords]);
  const isLoadingSuggestions = isLoadingReports || isLoadingFlis;

  const handleSuggestionSelected = (option: string | PartNumberSuggestion | null) => {
    if (!option || typeof option === 'string') {
      return;
    }
    if (option.kind === 'report') {
      navigate(getPath(`part-finder-reports/${option.report.id}`));
      return;
    }
    setPartNumber(option.record.nsn);
    setDescription(option.record.itemName ?? '');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(undefined);

    if (!partNumber.trim()) {
      setError(new Error('Part number is required'));
      return;
    }

    lookupToken.current += 1;
    const token = lookupToken.current;
    const submittedPartNumber = partNumber.trim();
    try {
      const listing = await searchOrListNsnCandidates({ partNumber: submittedPartNumber, description });
      if (listing && token === lookupToken.current) {
        setNsnListing({ partNumber: submittedPartNumber, listing });
      }
    } catch (error) {
      setError(error as ApiErrorDisplay);
    }
  };

  const runChosenSearches = (lookedUpPartNumber: string, { candidates, partNumberOnly }: NsnPickerChoice) => {
    const request = { partNumber: lookedUpPartNumber, description };
    if (candidates.length + Number(partNumberOnly) > 1) {
      return runSearchForNsns({ ...request, candidates, partNumberOnly });
    }
    if (partNumberOnly) {
      return runSearch({ ...request, partNumberOnly });
    }
    return runSearch({ ...request, niin: candidates[0].niin });
  };

  const handleNsnChoice = async (lookedUpPartNumber: string, choice: NsnPickerChoice) => {
    setError(undefined);
    try {
      await runChosenSearches(lookedUpPartNumber, choice);
    } catch (error) {
      setError(error as ApiErrorDisplay);
    }
  };

  const handlePartNumberChange = (value: string) => {
    setPartNumber(value);
    if (value.trim() !== nsnListing?.partNumber) {
      lookupToken.current += 1;
      setNsnListing(undefined);
    }
  };

  const apiError = getApiError(error, 'Failed to create part finder report');

  return (
    <FormCard
      onSubmit={event => {
        handleSubmit(event);
      }}
    >
      <ApiErrorAlert error={apiError} data-testid={PartFinderPageObjectModel.ErrorAlert} />
      <SearchRow>
        <Autocomplete
          freeSolo
          sx={{ flex: 1 }}
          options={options}
          loading={isLoadingSuggestions}
          inputValue={partNumber}
          onInputChange={(_event, value) => handlePartNumberChange(value)}
          onChange={(_event, value) => handleSuggestionSelected(value)}
          filterOptions={unfilteredOptions => unfilteredOptions}
          getOptionLabel={suggestionLabel}
          renderOption={({ key, ...optionProperties }, option) => {
            const report = typeof option !== 'string' && option.kind === 'report' ? option.report : undefined;
            const record = typeof option !== 'string' && option.kind === 'flis' ? option.record : undefined;
            const secondaryText = report?.description ?? record?.itemName;
            return (
              <SuggestionOption
                key={typeof option === 'string' ? key : suggestionKey(option)}
                {...optionProperties}
                data-testid={PartFinderPageObjectModel.PartNumberSuggestion}
              >
                <SuggestionText>
                  <Typography variant='body2'>{suggestionLabel(option)}</Typography>
                  {secondaryText && (
                    <SuggestionItemName variant='caption' color='text.secondary'>
                      {secondaryText}
                    </SuggestionItemName>
                  )}
                </SuggestionText>
                {report?.createdAt && (
                  <Chip size='small' label={`from ${format(new Date(report.createdAt), 'MM/dd/yyyy')}`} />
                )}
              </SuggestionOption>
            );
          }}
          renderInput={({ inputProps, ...parameters }) => (
            <TextField
              {...parameters}
              label='Part Number'
              placeholder='e.g., 1234-12-345-6789'
              helperText='Enter an NSN, NIIN, or any part number'
              required
              slotProps={{ htmlInput: { ...inputProps, 'data-testid': PartFinderPageObjectModel.PartNumberInput } }}
            />
          )}
        />
        <FindPartButton
          type='submit'
          variant='contained'
          color='primary'
          disabled={isPending || !partNumber.trim()}
          data-testid={PartFinderPageObjectModel.FindPartButton}
          startIcon={isPending ? undefined : <Search />}
        >
          {isPending ? (
            <CircularProgress size={20} data-testid={PartFinderPageObjectModel.LoadingSpinner} />
          ) : (
            'Find Part'
          )}
        </FindPartButton>
      </SearchRow>
      {nsnListing && (
        <NsnCandidatePicker
          partNumber={nsnListing.partNumber}
          listing={nsnListing.listing}
          isPending={isPending}
          onConfirm={choice => void handleNsnChoice(nsnListing.partNumber, choice)}
        />
      )}
      <TextField
        label='Description (Optional)'
        value={description}
        onChange={event => setDescription(event.target.value)}
        placeholder='e.g., Hydraulic pump assembly'
        multiline
        minRows={1}
        maxRows={4}
        fullWidth
        slotProps={{ htmlInput: { 'data-testid': PartFinderPageObjectModel.DescriptionInput } }}
      />
      <Divider />
      <FormFooter>
        <Typography variant='caption'>Have a list? Find up to 1000 part numbers at once.</Typography>
        <FooterActions>
          <FindMultipleLink
            type='button'
            onClick={() => partImageInputReference.current?.click()}
            data-testid='find-from-images-button'
          >
            <AddAPhotoOutlined fontSize='small' />
            Find from images
          </FindMultipleLink>
          <FindMultipleLink type='button' onClick={() => setFindMultipleOpen(true)} data-testid='find-multiple-button'>
            <PlaylistAddOutlined fontSize='small' />
            Find multiple
          </FindMultipleLink>
        </FooterActions>
      </FormFooter>
      <NewBatchModal open={findMultipleOpen} onClose={() => setFindMultipleOpen(false)} />
      <FindFromImages fileInputRef={partImageInputReference} />
    </FormCard>
  );
};
